import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from './supabaseClient';
import { fuzzySearchPlants } from './plantSearchUtils';

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
  'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
  'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
  'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
  'Wisconsin','Wyoming','Washington D.C.'
];

const STORES = [
  'Home Depot','Lowes','Trader Joe\'s','Costco','Walmart','IKEA',
  'Aldi','Whole Foods','Local Nursery','Garden Center','Other',
];

const emptySighting = () => ({
  common_name: '',
  genus: '',
  scientific_name: '',
  store: '',
  store_other: '',
  price: '',
  date_seen: '',
  notes: '',
  photo: null,
  photoPreview: null,
});

const SpottedInTheWild = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [photoConsent, setPhotoConsent] = useState(false);
  const [highlightedFields, setHighlightedFields] = useState({});
  const fieldRefs = useRef({});
  const [plantRecords, setPlantRecords] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const isMobile = window.innerWidth <= 600;

  const [location, setLocation] = useState({ city: '', state: '', country: 'USA' });
  const [sightings, setSightings] = useState([emptySighting()]);

  useEffect(() => {
    const fetchNames = async () => {
      const { data } = await supabase.from('plants').select('common_name, scientific_name').eq('status', 'approved');
      if (data) {
        setPlantRecords(data);
      }
    };
    fetchNames();
  }, []);

  const handleLocationChange = (e) => {
    setLocation({ ...location, [e.target.name]: e.target.value });
  };

  const handleSightingChange = (index, e) => {
    const updated = [...sightings];
    updated[index] = { ...updated[index], [e.target.name]: e.target.value };
    setSightings(updated);
    if (e.target.name === 'common_name') {
      const val = e.target.value;
      if (val.length > 0) {
        const matches = fuzzySearchPlants(val, plantRecords, 8);
        setSuggestions({ index, names: matches.map(m => m.common_name) });
      } else {
        setSuggestions(null);
      }
    }
  };

  const selectSuggestion = async (index, name) => {
    const updated = [...sightings];
    updated[index] = { ...updated[index], common_name: name };
    const { data } = await supabase.from('plants').select('genus, scientific_name').ilike('common_name', name.trim()).limit(1);
    if (data && data.length > 0) {
      if (data[0].genus) updated[index].genus = data[0].genus;
      if (data[0].scientific_name) updated[index].scientific_name = data[0].scientific_name;
    }
    setSightings(updated);
    setSuggestions(null);
  };

  const handlePhotoChange = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      const updated = [...sightings];
      updated[index] = { ...updated[index], photo: file, photoPreview: URL.createObjectURL(file) };
      setSightings(updated);
    }
  };

  const removePhoto = (index) => {
    const updated = [...sightings];
    updated[index] = { ...updated[index], photo: null, photoPreview: null };
    setSightings(updated);
  };

  const addSighting = () => setSightings([...sightings, emptySighting()]);
  const removeSighting = (index) => { if (sightings.length === 1) return; setSightings(sightings.filter((_, i) => i !== index)); };

  const handleSubmit = async () => {
    const missing = {};
    let firstMissingKey = null;
    sightings.forEach((s, i) => {
      if (!s.common_name) { missing[`${i}_common_name`] = true; if (!firstMissingKey) firstMissingKey = `${i}_common_name`; }
      if (!s.store) { missing[`${i}_store`] = true; if (!firstMissingKey) firstMissingKey = `${i}_store`; }
      if (!s.price) { missing[`${i}_price`] = true; if (!firstMissingKey) firstMissingKey = `${i}_price`; }
      if (!s.date_seen) { missing[`${i}_date_seen`] = true; if (!firstMissingKey) firstMissingKey = `${i}_date_seen`; }
    });
    if (Object.keys(missing).length > 0) {
      setHighlightedFields(missing);
      setError('Please fill in all required fields highlighted below.');
      if (firstMissingKey && fieldRefs.current[firstMissingKey]) {
        fieldRefs.current[firstMissingKey].scrollIntoView({ behavior: 'smooth', block: 'center' });
        fieldRefs.current[firstMissingKey].focus();
      }
      return;
    }
    const hasPhotos = sightings.some(s => s.photo);
    if (hasPhotos && !photoConsent) {
      setError('Please confirm you own the rights to the photo(s) you uploaded.');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const rows = [];
      for (const sighting of sightings) {
        let image_url = '';
        if (sighting.photo) {
          const fileExt = sighting.photo.name.split('.').pop();
          const fileName = `spotted_${Date.now()}_${sighting.common_name.replace(/\s+/g, '_')}.${fileExt}`;
          const { error: uploadError } = await supabase.storage.from('plant-images').upload(fileName, sighting.photo);
          if (uploadError) {
            setError('Photo upload failed. Please try again.');
            setLoading(false);
            return;
          }
          const { data: urlData } = supabase.storage.from('plant-images').getPublicUrl(fileName);
          image_url = urlData.publicUrl;
        }

        const storeName = sighting.store === 'Other' ? sighting.store_other : sighting.store;

        rows.push({
          common_name: sighting.common_name.trim(),
          genus: sighting.genus || null,
          scientific_name: sighting.scientific_name || null,
          store: storeName,
          price: sighting.price.trim(),
          date_seen: sighting.date_seen,
          city: location.city || null,
          state: location.state || null,
          country: location.country || 'USA',
          notes: sighting.notes || null,
          image_url: image_url || null,
          status: 'pending',
        });
      }

      const { error: insertError } = await supabase.from('store_sightings').insert(rows);
      if (insertError) {
        console.error('Insert error:', insertError);
        setError('Something went wrong: ' + insertError.message);
        setLoading(false);
        return;
      }
      setSubmitted(true);
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', border: '2px solid #c4a882',
    borderRadius: '10px', fontSize: '14px', background: '#ffffff',
    color: '#1e2d1f', outline: 'none', fontFamily: 'Lato, sans-serif',
    boxSizing: 'border-box', marginBottom: '12px',
  };

  const labelStyle = {
    display: 'block', fontSize: '10px', letterSpacing: '2px',
    textTransform: 'uppercase', color: '#8b6914', fontWeight: 700,
    marginBottom: '4px', fontFamily: 'Lato, sans-serif',
  };

  const sectionStyle = {
    background: '#ffffff', borderRadius: '20px', padding: '24px',
    boxShadow: '0 2px 16px rgba(44,74,46,0.1)', marginBottom: '24px',
    border: '1px solid rgba(143,185,154,0.3)',
  };

  if (submitted) {
    return (
      <div style={{ maxWidth: '600px', margin: '80px auto', padding: '0 16px', textAlign: 'center' }}>
        <div style={{ fontSize: '64px', marginBottom: '24px' }}>🔍</div>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '32px', color: '#1e3d20', marginBottom: '12px' }}>Thanks for the Spot!</h1>
        <p style={{ color: '#4a5e4b', fontFamily: 'Lato, sans-serif', fontSize: '16px', marginBottom: '32px' }}>
          Your {sightings.length} sighting{sightings.length !== 1 ? 's have' : ' has'} been submitted and will be reviewed before going live!
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => { setSightings([emptySighting()]); setSubmitted(false); }} style={{ background: 'transparent', border: '2px solid #8b6914', color: '#8b6914', borderRadius: '20px', padding: '12px 24px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
            Submit Another
          </button>
          <button onClick={() => navigate('/')} style={{ background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', color: '#f5f0e8', border: 'none', borderRadius: '20px', padding: '12px 24px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', padding: '40px 16px 80px' }}>
      <div style={{ marginBottom: '32px' }}>
        <div style={{ fontSize: '11px', letterSpacing: '4px', color: '#8b6914', textTransform: 'uppercase', marginBottom: '8px', fontFamily: 'Lato, sans-serif' }}>🔍 Community Sightings</div>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '36px', color: '#1e3d20', marginBottom: '8px' }}>Spotted in the Wild</h1>
        <p style={{ color: '#4a5e4b', fontFamily: 'Lato, sans-serif', fontSize: '15px' }}>
          Found a great plant deal at Home Depot, Lowes, Trader Joe's or another store? Share it with the community!
        </p>
      </div>

      {sightings.map((sighting, index) => (
        <div key={index} style={{ ...sectionStyle, borderTop: '4px solid #8b6914' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1e3d20', margin: 0 }}>🏪 Sighting #{index + 1}</h2>
            {sightings.length > 1 && (
              <button onClick={() => removeSighting(index)} style={{ background: 'transparent', border: '1px solid #c0392b', color: '#c0392b', borderRadius: '20px', padding: '4px 14px', fontSize: '12px', cursor: 'pointer', fontFamily: 'Lato, sans-serif', fontWeight: 700 }}>Remove</button>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 16px' }}>

            <div style={{ position: 'relative', gridColumn: isMobile ? '1' : '1 / -1' }}>
              <label style={labelStyle}>Plant Name *</label>
              <input name="common_name" value={sighting.common_name} ref={el => fieldRefs.current[`${index}_common_name`] = el} onChange={(e) => { handleSightingChange(index, e); setHighlightedFields(h => ({...h, [`${index}_common_name`]: false})); }}
                onBlur={() => setTimeout(() => setSuggestions(null), 150)}
                placeholder="Search or type plant name..." style={inputStyle} autoComplete="off" />
              {suggestions && suggestions.index === index && suggestions.names.length > 0 && (
                <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '2px solid #8b6914', borderRadius: '10px', listStyle: 'none', margin: '-8px 0 0', padding: '6px', zIndex: 1000, maxHeight: '200px', overflowY: 'auto', boxShadow: '0 8px 24px rgba(44,74,46,0.15)' }}>
                  {suggestions.names.map((name) => (
                    <li key={name} onMouseDown={() => selectSuggestion(index, name)}
                      style={{ padding: '8px 12px', cursor: 'pointer', borderRadius: '6px', color: '#1e2d1f', fontSize: '13px', fontFamily: 'Lato, sans-serif' }}
                      onMouseEnter={(e) => e.target.style.background = '#fdf8f0'}
                      onMouseLeave={(e) => e.target.style.background = 'transparent'}>{name}</li>
                  ))}
                  {!suggestions.names.includes(sighting.common_name) && sighting.common_name && (
                    <li onMouseDown={() => selectSuggestion(index, sighting.common_name)}
                      style={{ padding: '8px 12px', cursor: 'pointer', borderRadius: '6px', color: '#8b6914', fontSize: '13px', fontFamily: 'Lato, sans-serif', borderTop: '1px solid rgba(139,105,20,0.2)', fontWeight: 700 }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#fdf8f0'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>+ Add "{sighting.common_name}" as new plant</li>
                  )}
                </ul>
              )}
            </div>

            <div>
              <label style={labelStyle}>Store *</label>
              <select name="store" value={sighting.store} ref={el => fieldRefs.current[`${index}_store`] = el} onChange={(e) => { handleSightingChange(index, e); setHighlightedFields(h => ({...h, [`${index}_store`]: false})); }} style={{ ...inputStyle, border: highlightedFields[`${index}_store`] ? '2px solid #c0392b' : '2px solid #c4a882', background: highlightedFields[`${index}_store`] ? '#fff5f5' : '#ffffff' }}>
                <option value="">Select store...</option>
                {STORES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {sighting.store === 'Other' && (
              <div>
                <label style={labelStyle}>Store Name</label>
                <input name="store_other" value={sighting.store_other} onChange={(e) => handleSightingChange(index, e)} placeholder="e.g. Green Thumb Nursery" style={inputStyle} />
              </div>
            )}

            <div>
              <label style={labelStyle}>Price Seen *</label>
              <input name="price" value={sighting.price} ref={el => fieldRefs.current[`${index}_price`] = el} onChange={(e) => { handleSightingChange(index, e); setHighlightedFields(h => ({...h, [`${index}_price`]: false})); }} placeholder="e.g. $12.99" style={{ ...inputStyle, border: highlightedFields[`${index}_price`] ? '2px solid #c0392b' : '2px solid #c4a882', background: highlightedFields[`${index}_price`] ? '#fff5f5' : '#ffffff' }} />
            </div>

            <div>
              <label style={labelStyle}>Date Seen *</label>
              <input name="date_seen" type="date" value={sighting.date_seen} ref={el => fieldRefs.current[`${index}_date_seen`] = el} onChange={(e) => { handleSightingChange(index, e); setHighlightedFields(h => ({...h, [`${index}_date_seen`]: false})); }} style={{ ...inputStyle, fontSize: '13px', minHeight: '44px', border: highlightedFields[`${index}_date_seen`] ? '2px solid #c0392b' : '2px solid #c4a882', background: highlightedFields[`${index}_date_seen`] ? '#fff5f5' : '#ffffff' }} />
            </div>

            <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
              <label style={labelStyle}>Notes (optional)</label>
              <input name="notes" value={sighting.notes} onChange={(e) => handleSightingChange(index, e)} placeholder="e.g. 4&quot; pot, looked healthy, about 10 left in stock..." style={inputStyle} />
            </div>
          </div>

          <label style={{ ...labelStyle, marginTop: '4px' }}>Photo (encouraged! 📸)</label>
          <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '2px dashed #8b6914', borderRadius: '12px', padding: '20px', cursor: 'pointer', background: '#fdf8f0' }}>
            <input type="file" accept="image/*" onChange={(e) => handlePhotoChange(index, e)} style={{ display: 'none' }} />
            {sighting.photoPreview ? (
              <img src={sighting.photoPreview} alt="Preview" style={{ maxHeight: '140px', maxWidth: '100%', borderRadius: '8px', objectFit: 'cover' }} />
            ) : (
              <>
                <div style={{ fontSize: '28px', marginBottom: '8px' }}>📸</div>
                <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#8b6914', fontWeight: 700 }}>Click to upload a photo of the price tag!</div>
              </>
            )}
          </label>
          {sighting.photoPreview && (
            <button onClick={() => removePhoto(index)} style={{ marginTop: '8px', background: 'transparent', border: 'none', color: '#c0392b', cursor: 'pointer', fontFamily: 'Lato, sans-serif', fontSize: '12px' }}>Remove photo</button>
          )}
        </div>
      ))}

      <button onClick={addSighting} style={{ width: '100%', padding: '14px', background: 'transparent', border: '2px dashed #8b6914', color: '#8b6914', borderRadius: '16px', fontSize: '14px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Lato, sans-serif', marginBottom: '24px' }}
        onMouseEnter={(e) => e.target.style.background = '#fdf8f0'}
        onMouseLeave={(e) => e.target.style.background = 'transparent'}>
        + Add Another Sighting
      </button>

      <div style={sectionStyle}>
        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1e3d20', marginBottom: '8px' }}>📍 Location</h2>
        <p style={{ fontSize: '13px', color: '#4a5e4b', fontFamily: 'Lato, sans-serif', marginBottom: '20px' }}>Where did you spot it? Applied to all sightings.</p>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '0 16px' }}>
          <div>
            <label style={labelStyle}>City (optional)</label>
            <input name="city" value={location.city} onChange={handleLocationChange} placeholder="e.g. Miami" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>State</label>
            <select name="state" value={location.state} onChange={handleLocationChange} style={inputStyle}>
              <option value="">Select state...</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              <option value="International">International</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Country</label>
            <select name="country" value={location.country} onChange={handleLocationChange} style={inputStyle}>
              <option value="USA">USA</option>
              <option value="Canada">Canada</option>
              <option value="UK">UK</option>
              <option value="Australia">Australia</option>
              <option value="Germany">Germany</option>
              <option value="France">France</option>
              <option value="Netherlands">Netherlands</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>
      </div>

      {sightings.some(s => s.photo) && (
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '16px', padding: '14px 16px', background: '#fdf8f0', borderRadius: '12px', cursor: 'pointer' }}>
          <input type="checkbox" checked={photoConsent} onChange={(e) => setPhotoConsent(e.target.checked)} style={{ marginTop: '3px', width: '16px', height: '16px', flexShrink: 0 }} />
          <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b', lineHeight: 1.5 }}>
            I confirm I took these photos myself (or have permission to use them) and grant True Plant Value permission to display them on the site.
          </span>
        </label>
      )}

      {error && <p style={{ color: '#c0392b', fontFamily: 'Lato, sans-serif', marginBottom: '16px', padding: '12px', background: '#fff5f5', borderRadius: '10px', border: '1px solid #c0392b' }}>{error}</p>}

      <button onClick={handleSubmit} disabled={loading} style={{ width: '100%', padding: '16px', background: loading ? '#c4a882' : 'linear-gradient(135deg, #8b6914, #c4a882)', color: '#ffffff', border: 'none', borderRadius: '16px', fontSize: '15px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'Lato, sans-serif' }}>
        {loading ? 'Submitting...' : `Submit ${sightings.length} Sighting${sightings.length !== 1 ? 's' : ''} 🔍`}
      </button>
    </div>
  );
};

export default SpottedInTheWild;